import moment from 'moment'

import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { EmptyArray } from 'lib/array/arrayUtils'
import {
  isBedbankOrderItemPastOrCancelled,
  isHotelOrderItemPastOrCancelled,
} from 'lib/order/isUpcoming'

export type ReviewStayProvider = 'luxuryescapes' | 'bedbank'

export type ReviewStayAction = 'leave' | 'view'

export interface ReviewStayDateRange {
  startDate?: string
  endDate?: string
}

export interface ReviewableStay {
  /** URL/session key. After LE+bedbank merge this becomes `luxuryescapes:{lePropertyId}`. */
  stayKey: string
  itemId: string
  itemIds: Array<string>
  /** Inventory channel of the representative item. Not stored on the review row. */
  provider: ReviewStayProvider
  orderId: string
  /** Id we submit on create. Merged LE+bedbank groups prefer the LE property id. */
  propertyId?: string
  /**
   * All property ids that should match this stay's review, including mapped LE and bedbank ids.
   * This is the frontend stand-in for "GET ?orderId= should expand related ids".
   */
  reviewPropertyIds: Array<string>
  offerId?: string
  /** All offers in this hotel group, so an old review keyed only by offerId still matches. */
  reviewOfferIds: Array<string>
  title: string
  imageId?: string
  startDate?: string
  endDate?: string
  dateRanges: Array<ReviewStayDateRange>
}

export interface ReviewableStayRow {
  stay: ReviewableStay
  review?: App.Review
  action?: ReviewStayAction
}

export type OrderReviewLoadStatus = 'unloaded' | 'loading' | 'error' | 'ready'

export interface OrderReviewProgress {
  status: OrderReviewLoadStatus
  eligibleCount: number
  reviewedCount: number
  rows: Array<ReviewableStayRow>
}

export interface OrderReviewLoadState {
  reviews?: Array<App.Review>
  loading: boolean
  error: boolean
}

const EMPTY_READY_PROGRESS: OrderReviewProgress = {
  status: 'ready',
  eligibleCount: 0,
  reviewedCount: 0,
  rows: EmptyArray,
}

export const EMPTY_UNLOADED_PROGRESS: OrderReviewProgress = {
  status: 'unloaded',
  eligibleCount: 0,
  reviewedCount: 0,
  rows: EmptyArray,
}

export function buildStayKey(provider: ReviewStayProvider, identity: string): string {
  return `${provider}:${identity}`
}

export function parseStayKey(stayKey: string): { provider: ReviewStayProvider, identity: string } | undefined {
  const separatorIndex = stayKey.indexOf(':')
  if (separatorIndex <= 0 || separatorIndex === stayKey.length - 1) return undefined

  const provider = stayKey.slice(0, separatorIndex)
  const identity = stayKey.slice(separatorIndex + 1)

  if (provider !== 'luxuryescapes' && provider !== 'bedbank') return undefined
  if (!identity) return undefined

  return { provider, identity }
}

export function normalizeReviewCalendarDate(value?: string | moment.Moment): string | undefined {
  if (!value) return undefined

  if (moment.isMoment(value)) {
    // Bedbank dates are local-midnight calendar values, not instants to convert to UTC.
    return value.isValid() ? value.format(ISO_DATE_FORMAT) : undefined
  }

  const dateValue = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
    return dateValue.slice(0, 10)
  }

  const parsed = moment.utc(dateValue)
  return parsed.isValid() ? parsed.format(ISO_DATE_FORMAT) : undefined
}

export function getReviewStayNights(startDate: string, endDate: string): number {
  return Math.max(moment.utc(endDate, ISO_DATE_FORMAT, true).diff(moment.utc(startDate, ISO_DATE_FORMAT, true), 'days'), 0)
}

function isLuxuryEscapesHotelItem(item: App.OrderItem): boolean {
  return item.reservationMade && !!item.offer
}

function isBedbankHotelItem(item: App.OrderBedbankItem): boolean {
  return !!item.offer && !(item.downgradeType && item.isDowngraded)
}

function isLuxuryEscapesStayReviewable(item: App.OrderItem): boolean {
  if (item.status !== 'completed' || !item.reservationMade || !item.reservation) {
    return false
  }

  return isHotelOrderItemPastOrCancelled(item)
}

function isBedbankStayReviewable(item: App.OrderBedbankItem): boolean {
  if (item.status !== 'completed' || (item.downgradeType && item.isDowngraded)) {
    return false
  }

  return isBedbankOrderItemPastOrCancelled(item)
}

function getLuxuryEscapesPropertyId(item: App.OrderItem): string | undefined {
  if (item.offer.property?.id) return item.offer.property.id
  if (item.package && 'propertyId' in item.package) return item.package.propertyId
  return undefined
}

function getLuxuryEscapesReviewPropertyIds(item: App.OrderItem): Array<string> {
  const packagePropertyId = item.package && 'propertyId' in item.package ? item.package.propertyId : undefined
  return uniqueIds([item.offer.property?.id, packagePropertyId])
}

function uniqueIds(ids: Array<string | undefined>): Array<string> {
  return [...new Set(ids.filter((id): id is string => !!id))]
}

function reviewPropertyIdsForStay(propertyId?: string): Array<string> {
  return propertyId ? [propertyId] : []
}

function stayMatchesOfferId(stay: ReviewableStay, offerId: string): boolean {
  return stay.reviewOfferIds.includes(offerId) || stay.offerId === offerId
}

function mappingMatchesStay(mapping: App.PropertyOfferMapping, stay: ReviewableStay): boolean {
  if (stay.provider === 'luxuryescapes') {
    return (!!stay.propertyId && mapping.lpcPropertyIds.includes(stay.propertyId)) ||
      stay.reviewOfferIds.some(offerId => mapping.lpcOfferIds.includes(offerId))
  }

  return (!!stay.propertyId && mapping.bedbankPropertyIds.includes(stay.propertyId)) ||
    stay.reviewOfferIds.some(offerId => mapping.bedbankPropertyIds.includes(offerId))
}

function accommodationGroupKey(
  stay: ReviewableStay,
  mappings: Array<App.PropertyOfferMapping>,
): string {
  const mapping = mappings.find(candidate => mappingMatchesStay(candidate, stay))
  if (mapping?.propertyId) return `accommodation:${mapping.propertyId}`
  return stay.stayKey
}

function mergeStayGroups(
  groups: Array<ReviewableStay>,
  mappings: Array<App.PropertyOfferMapping>,
): ReviewableStay {
  const sortedGroups = [...groups].sort(compareRepresentativeStays)
  const dateRanges = uniqueDateRanges(sortedGroups)
  const luxuryEscapesGroup = sortedGroups.find(group => group.provider === 'luxuryescapes')
  const matchingMapping = mappings.find(mapping => sortedGroups.some(group => mappingMatchesStay(mapping, group)))
  const lePropertyId = luxuryEscapesGroup?.propertyId ?? matchingMapping?.lpcPropertyIds[0]
  const representative = luxuryEscapesGroup ?? sortedGroups[0]
  const reviewPropertyIds = uniqueIds([
    lePropertyId,
    ...sortedGroups.flatMap(group => group.reviewPropertyIds),
    ...(matchingMapping?.lpcPropertyIds ?? []),
    ...(matchingMapping?.bedbankPropertyIds ?? []),
  ])
  const reviewOfferIds = uniqueIds(sortedGroups.flatMap(group => group.reviewOfferIds))

  if (lePropertyId) {
    return {
      ...representative,
      stayKey: buildStayKey('luxuryescapes', lePropertyId),
      provider: 'luxuryescapes',
      propertyId: lePropertyId,
      reviewPropertyIds,
      reviewOfferIds,
      itemIds: sortedGroups.flatMap(group => group.itemIds),
      dateRanges,
      startDate: dateRanges[0]?.startDate,
      endDate: dateRanges[0]?.endDate,
    }
  }

  return {
    ...representative,
    reviewPropertyIds,
    reviewOfferIds,
    itemIds: sortedGroups.flatMap(group => group.itemIds),
    dateRanges,
    startDate: dateRanges[0]?.startDate,
    endDate: dateRanges[0]?.endDate,
  }
}

function hotelGroupIdentity(stay: Pick<ReviewableStay, 'propertyId' | 'offerId' | 'itemId'>): string {
  if (stay.propertyId) return stay.propertyId
  if (stay.offerId) return `offer:${stay.offerId}`
  return `item:${stay.itemId}`
}

function compareRepresentativeStays(left: ReviewableStay, right: ReviewableStay): number {
  const startCompare = (left.startDate ?? '').localeCompare(right.startDate ?? '')
  if (startCompare !== 0) return startCompare
  if (left.provider !== right.provider) {
    return left.provider === 'luxuryescapes' ? -1 : 1
  }
  return left.itemId.localeCompare(right.itemId)
}

function dateRangeKey(range: ReviewStayDateRange): string {
  return `${range.startDate ?? ''}|${range.endDate ?? ''}`
}

function uniqueDateRanges(stays: Array<ReviewableStay>): Array<ReviewStayDateRange> {
  const rangesByKey = new Map<string, ReviewStayDateRange>()

  stays.forEach((stay) => {
    const ranges = stay.dateRanges.length > 0
      ? stay.dateRanges
      : [{ startDate: stay.startDate, endDate: stay.endDate }]

    ranges.forEach((range) => {
      const key = dateRangeKey(range)
      if (!rangesByKey.has(key)) {
        rangesByKey.set(key, range)
      }
    })
  })

  return [...rangesByKey.values()].sort((left, right) => {
    const startCompare = (left.startDate ?? '').localeCompare(right.startDate ?? '')
    if (startCompare !== 0) return startCompare
    return (left.endDate ?? '').localeCompare(right.endDate ?? '')
  })
}

function mapLuxuryEscapesStay(order: App.Order, item: App.OrderItem): ReviewableStay {
  const startDate = normalizeReviewCalendarDate(item.reservation?.startDate)
  const endDate = normalizeReviewCalendarDate(item.reservation?.endDate)

  return {
    stayKey: '',
    itemId: item.id,
    itemIds: [item.id],
    provider: 'luxuryescapes',
    orderId: order.id,
    propertyId: getLuxuryEscapesPropertyId(item),
    reviewPropertyIds: getLuxuryEscapesReviewPropertyIds(item),
    offerId: item.offerId,
    reviewOfferIds: uniqueIds([item.offerId]),
    title: item.offer.property?.name ?? item.offer.name,
    imageId: item.offer.image?.id,
    startDate,
    endDate,
    dateRanges: [{ startDate, endDate }],
  }
}

function mapBedbankStay(order: App.Order, item: App.OrderBedbankItem): ReviewableStay {
  const startDate = normalizeReviewCalendarDate(item.checkIn)
  const endDate = normalizeReviewCalendarDate(item.checkOut)

  return {
    stayKey: '',
    itemId: item.id,
    itemIds: [item.id],
    provider: 'bedbank',
    orderId: order.id,
    propertyId: item.propertyId,
    reviewPropertyIds: reviewPropertyIdsForStay(item.propertyId),
    offerId: item.offer.id,
    reviewOfferIds: uniqueIds([item.offer.id]),
    title: item.offer.name,
    imageId: item.offer.image?.id,
    startDate,
    endDate,
    dateRanges: [{ startDate, endDate }],
  }
}

function groupEligibleStays(
  itemStays: Array<ReviewableStay>,
  mappings: Array<App.PropertyOfferMapping> = [],
): Array<ReviewableStay> {
  const staysByGroup = new Map<string, Array<ReviewableStay>>()

  itemStays.forEach((stay) => {
    const groupKey = buildStayKey(stay.provider, hotelGroupIdentity(stay))
    const groupedStays = staysByGroup.get(groupKey) ?? []
    groupedStays.push(stay)
    staysByGroup.set(groupKey, groupedStays)
  })

  const providerGroups = [...staysByGroup.entries()].map(([stayKey, groupedStays]) => {
    const sortedStays = [...groupedStays].sort(compareRepresentativeStays)
    const representative = sortedStays[0]
    const dateRanges = uniqueDateRanges(sortedStays)

    return {
      ...representative,
      stayKey,
      itemIds: sortedStays.map(stay => stay.itemId),
      reviewPropertyIds: uniqueIds(sortedStays.flatMap(stay => stay.reviewPropertyIds)),
      reviewOfferIds: uniqueIds(sortedStays.flatMap(stay => stay.reviewOfferIds)),
      dateRanges,
      startDate: dateRanges[0]?.startDate,
      endDate: dateRanges[0]?.endDate,
    }
  })

  if (mappings.length === 0) {
    return providerGroups.sort(compareRepresentativeStays)
  }

  const staysByAccommodation = new Map<string, Array<ReviewableStay>>()
  providerGroups.forEach((stay) => {
    const groupKey = accommodationGroupKey(stay, mappings)
    const groupedStays = staysByAccommodation.get(groupKey) ?? []
    groupedStays.push(stay)
    staysByAccommodation.set(groupKey, groupedStays)
  })

  return [...staysByAccommodation.values()]
    .map(groupedStays => groupedStays.length === 1 ? groupedStays[0] : mergeStayGroups(groupedStays, mappings))
    .sort(compareRepresentativeStays)
}

export interface StayPropertyMappingLookup {
  offerType: typeof OFFER_TYPE_ALWAYS_ON | typeof OFFER_TYPE_BED_BANK
  lookupId: string
}

export function getOrderStayMappingLookups(order: App.Order): Array<StayPropertyMappingLookup> {
  const stays = getReviewableStays(order)
  const hasLuxuryEscapes = stays.some(stay => stay.provider === 'luxuryescapes')
  const hasBedbank = stays.some(stay => stay.provider === 'bedbank')
  if (!hasLuxuryEscapes || !hasBedbank) return []

  const lookups: Array<StayPropertyMappingLookup> = []
  stays.forEach((stay) => {
    if (stay.provider === 'luxuryescapes') {
      stay.reviewOfferIds.forEach((offerId) => {
        lookups.push({ offerType: OFFER_TYPE_ALWAYS_ON, lookupId: offerId })
      })
    }
    if (stay.provider === 'bedbank') {
      if (stay.propertyId) lookups.push({ offerType: OFFER_TYPE_BED_BANK, lookupId: stay.propertyId })
      stay.reviewOfferIds.forEach((offerId) => {
        if (offerId !== stay.propertyId) {
          lookups.push({ offerType: OFFER_TYPE_BED_BANK, lookupId: offerId })
        }
      })
    }
  })

  return lookups.filter((lookup, index) => lookups.findIndex(candidate => candidate.lookupId === lookup.lookupId) === index)
}

export function areOrderStayMappingsReady(
  lookups: Array<StayPropertyMappingLookup>,
  propertyOfferMappings: App.OfferState['propertyOfferMappings'],
): boolean {
  if (lookups.length === 0) return true
  return lookups.every((lookup) => {
    const record = propertyOfferMappings[lookup.lookupId]
    return !!record && !record.fetching
  })
}

export function hasOrderStayMappingFailure(
  lookups: Array<StayPropertyMappingLookup>,
  propertyOfferMappings: App.OfferState['propertyOfferMappings'],
): boolean {
  if (lookups.length === 0) return false
  if (!areOrderStayMappingsReady(lookups, propertyOfferMappings)) return false

  return lookups.some((lookup) => !!propertyOfferMappings[lookup.lookupId]?.error)
}

function firstReviewableStay(stays: Array<ReviewableStay>): Array<ReviewableStay> {
  if (stays.length <= 1) return stays
  return [stays[0]]
}

export function getResolvedOrderStayMappings(
  lookups: Array<StayPropertyMappingLookup>,
  propertyOfferMappings: App.OfferState['propertyOfferMappings'],
): Array<App.PropertyOfferMapping> {
  const mappings = lookups
    .map(lookup => propertyOfferMappings[lookup.lookupId]?.mapping)
    .filter((mapping): mapping is App.PropertyOfferMapping => !!mapping)

  return mappings.filter((mapping, index) => mappings.findIndex(candidate => candidate.propertyId === mapping.propertyId) === index)
}

export function getReviewableStays(
  order: App.Order,
  mappings: Array<App.PropertyOfferMapping> = [],
): Array<ReviewableStay> {
  const luxuryEscapesStays = order.items
    .filter(isLuxuryEscapesStayReviewable)
    .map(item => mapLuxuryEscapesStay(order, item))
  const bedbankStays = order.bedbankItems
    .filter(isBedbankStayReviewable)
    .map(item => mapBedbankStay(order, item))

  return groupEligibleStays([...luxuryEscapesStays, ...bedbankStays], mappings)
}

export function getOrderHotelStays(
  order: App.Order,
  mappings: Array<App.PropertyOfferMapping> = [],
): Array<ReviewableStay> {
  const luxuryEscapesStays = order.items
    .filter(isLuxuryEscapesHotelItem)
    .map(item => mapLuxuryEscapesStay(order, item))
  const bedbankStays = order.bedbankItems
    .filter(isBedbankHotelItem)
    .map(item => mapBedbankStay(order, item))

  return groupEligibleStays([...luxuryEscapesStays, ...bedbankStays], mappings)
}

function isAccommodationReview(review: App.Review): boolean {
  return !review.tourId && !review.experienceId
}

function pickCanonicalReview(reviews: Array<App.Review>): App.Review {
  return [...reviews].sort((left, right) => {
    const createdCompare = (right.createdAt ?? '').localeCompare(left.createdAt ?? '')
    if (createdCompare !== 0) return createdCompare
    return (left.id ?? '').localeCompare(right.id ?? '')
  })[0]
}

function reviewsForStay(
  stay: ReviewableStay,
  reviews: Array<App.Review>,
  stays: Array<ReviewableStay>,
): Array<App.Review> {
  const propertyMatches = stay.reviewPropertyIds.length > 0
    ? reviews.filter(review => !!review.propertyId && stay.reviewPropertyIds.includes(review.propertyId))
    : []

  if (propertyMatches.length > 0) return propertyMatches

  if (stay.reviewOfferIds.length === 0) return []

  return reviews.filter((review) => {
    if (review.propertyId || !review.offerId || !stay.reviewOfferIds.includes(review.offerId)) {
      return false
    }

    const staysWithSameOffer = stays.filter(candidate => stayMatchesOfferId(candidate, review.offerId!))
    return staysWithSameOffer.length === 1
  })
}

function reviewConflictsWithStay(review: App.Review, stay: ReviewableStay): boolean {
  if (review.propertyId && !stay.reviewPropertyIds.includes(review.propertyId)) return true
  if (review.offerId && stay.reviewOfferIds.length > 0 && !stay.reviewOfferIds.includes(review.offerId)) return true
  return false
}

function isIdentityLessReview(review: App.Review): boolean {
  return !review.propertyId && !review.offerId
}

export function associateStayReviews(
  stays: Array<ReviewableStay>,
  reviews: Array<App.Review>,
  allHotels: Array<ReviewableStay> = stays,
): Array<ReviewableStayRow> {
  const accommodationReviews = reviews.filter(isAccommodationReview)
  const assignedReviewIds = new Set<string>()

  const rows = stays.map((stay) => {
    const matchingReviews = reviewsForStay(stay, accommodationReviews, stays)
      .filter(review => !review.id || !assignedReviewIds.has(review.id))

    if (matchingReviews.length > 0) {
      const review = pickCanonicalReview(matchingReviews)
      if (review.id) assignedReviewIds.add(review.id)
      return { stay, review, action: 'view' as ReviewStayAction }
    }

    return { stay, action: 'leave' as ReviewStayAction }
  })

  if (stays.length !== 1 || allHotels.length !== 1) return rows

  const unmatchedReviews = accommodationReviews.filter(review => !review.id || !assignedReviewIds.has(review.id))
  if (unmatchedReviews.length === 0 || rows[0].action !== 'leave') return rows

  const stay = rows[0].stay
  if (unmatchedReviews.some(review => reviewConflictsWithStay(review, stay))) return rows

  const identityLessReviews = unmatchedReviews.filter(isIdentityLessReview)
  if (identityLessReviews.length === 0) return rows

  const review = pickCanonicalReview(identityLessReviews)
  if (review.id) assignedReviewIds.add(review.id)
  return [{ stay, review, action: 'view' }]
}

export function buildOrderReviewProgress(
  order: App.Order,
  loadState: OrderReviewLoadState,
  mappings: Array<App.PropertyOfferMapping> = [],
  mappingsReady = true,
  mappingFailed = false,
): OrderReviewProgress {
  const stays = mappingFailed
    ? firstReviewableStay(getReviewableStays(order))
    : getReviewableStays(order, mappingsReady ? mappings : [])

  if (stays.length === 0 && loadState.reviews !== undefined && !loadState.loading && !loadState.error) {
    return EMPTY_READY_PROGRESS
  }

  if (loadState.loading) {
    return {
      status: 'loading',
      eligibleCount: stays.length,
      reviewedCount: 0,
      rows: stays.map(stay => ({ stay })),
    }
  }

  if (loadState.error) {
    return {
      status: 'error',
      eligibleCount: stays.length,
      reviewedCount: 0,
      rows: stays.map(stay => ({ stay })),
    }
  }

  if (!mappingsReady) {
    return {
      status: 'loading',
      eligibleCount: stays.length,
      reviewedCount: 0,
      rows: stays.map(stay => ({ stay })),
    }
  }

  if (loadState.reviews === undefined) {
    return {
      status: 'unloaded',
      eligibleCount: stays.length,
      reviewedCount: 0,
      rows: stays.map(stay => ({ stay })),
    }
  }

  if (stays.length === 0) {
    return EMPTY_READY_PROGRESS
  }

  const resolvedMappings = mappingFailed || !mappingsReady ? [] : mappings
  const allHotels = getOrderHotelStays(order, resolvedMappings)
  const rows = associateStayReviews(stays, loadState.reviews, allHotels)
  const reviewedCount = rows.filter(row => row.action === 'view').length

  return {
    status: 'ready',
    eligibleCount: stays.length,
    reviewedCount,
    rows,
  }
}

export function findOrderStay(
  order: App.Order,
  stayKey: string,
  mappings: Array<App.PropertyOfferMapping> = [],
): ReviewableStay | undefined {
  const stays = getReviewableStays(order, mappings)
  const matchedByKey = stays.find(stay => stay.stayKey === stayKey)
  if (matchedByKey) return matchedByKey

  const parsed = parseStayKey(stayKey)
  if (!parsed) return undefined

  return stays.find((stay) => {
    if (stay.propertyId === parsed.identity) return true
    if (stay.reviewPropertyIds.includes(parsed.identity)) return true
    if (stay.reviewOfferIds.some(offerId => parsed.identity === `offer:${offerId}`)) return true
    if (stay.itemIds.includes(parsed.identity)) return true
    if (parsed.identity.startsWith('item:') && stay.itemIds.includes(parsed.identity.slice(5))) return true
    return false
  })
}

export interface ReviewFormOrderItem {
  accommodationItem?: App.OrderItem
  bedbankItem?: App.OrderBedbankItem
  experienceItem?: App.OrderExperienceItem
  tourItem?: App.Tours.TourV2OrderItem
}

export function getReviewFormOrderItem(
  order: App.Order,
  stay?: ReviewableStay,
): ReviewFormOrderItem | undefined {
  const luxuryEscapesItem = stay
    ? order.items.find(item => item.id === stay.itemId) ??
    order.items.find(item => stay.itemIds.includes(item.id))
    : undefined
  const bedbankItemFromStay = stay
    ? order.bedbankItems.find(item => item.id === stay.itemId) ??
    order.bedbankItems.find(item => stay.itemIds.includes(item.id))
    : undefined

  if (stay?.provider === 'luxuryescapes' || luxuryEscapesItem) {
    if (!luxuryEscapesItem) return undefined

    return {
      accommodationItem: luxuryEscapesItem,
      bedbankItem: bedbankItemFromStay,
      experienceItem: order.experienceItems[0],
      tourItem: order.tourItems[0],
    }
  }

  if (stay?.provider === 'bedbank') {
    if (!bedbankItemFromStay) return undefined

    return {
      bedbankItem: bedbankItemFromStay,
      experienceItem: order.experienceItems[0],
      tourItem: order.tourItems[0],
    }
  }

  return {
    accommodationItem: order.items[0],
    bedbankItem: order.bedbankItems[0],
    experienceItem: order.experienceItems[0],
    tourItem: order.tourItems[0],
  }
}

export function getHotelTypeForStay(order: App.Order, stay?: ReviewableStay): App.OfferType | undefined {
  if (stay) {
    const luxuryEscapesItem = order.items.find(item => stay.itemIds.includes(item.id))
    if (luxuryEscapesItem) return luxuryEscapesItem.offer.type
    if (stay.provider === 'bedbank') return 'bedbank_hotel'
  }

  if (order.bedbankItems.length && !order.items.length) return 'bedbank_hotel'
  return order.items[0]?.offer.type
}
